import classNames from "classnames"
import React, { PropsWithChildren, useMemo } from "react"

import { Color } from "../../../constants/V2/color"
import PillButton from "../../elements/V2/Buttons/PillButton"
import TableSectionTitles from "../../elements/V2/TableSectionTitles"
import Typography from "../../elements/V2/Typography"
import LayoutSection from "../LayoutComponents/LayoutSection"
import DoubleEntryComparisonTable from "../Sections/DoubleEntryComparisonTable"
import SingleEntryComparisonTable from "../Sections/SingleEntryComparisonTable"

import { getAccentColor } from "@utils/V2/color"

type Props = PropsWithChildren<{
  sectionId: string
  headingSize?: "small" | "large"
  headingSecondaryText?: string
  overrideHeadingSecondaryColor?: Color
  headingPrimaryText: string
  overrideHeadingPrimaryColor?: Color
  backgroundColor: Color.Black | Color.Charcoal | Color.White | Color.MintGreen
  tableNumDesktopColumns: 3 | 4 | 6
  tableColumnImages?: string[]
  tableRowHeadings: string[]
  tableData: string[][]
  disclaimerText?: string
  buttonText?: string
  buttonBorderColor?: Color
  buttonLink?: string
  variant?: "single-entry" | "double-entry"
  outerContainer?: "gridSection" | "tabSelector"
  id?: string
}> &
  Partial<Storyblok.CTATrackingEvent>

const ComparisonTableSection = ({
  sectionId,
  headingSize = "large",
  headingSecondaryText,
  overrideHeadingSecondaryColor,
  headingPrimaryText,
  overrideHeadingPrimaryColor,
  backgroundColor,
  tableNumDesktopColumns,
  tableColumnImages,
  tableRowHeadings,
  tableData,
  disclaimerText,
  buttonBorderColor,
  buttonText,
  buttonLink,
  variant = "single-entry",
  outerContainer = "gridSection",
  trackingEvent,
  trackingEventKey,
  trackingEventValue,
  id,
  ...props
}: Props) => {
  const columnCount = useMemo(() => {
    let longestDataRow = 0
    tableData.forEach((dataRow) => {
      if (dataRow.length > longestDataRow) {
        longestDataRow = dataRow.length
      }
    })
    return longestDataRow
  }, [tableData])

  return (
    <LayoutSection
      primaryBackgroundColor={backgroundColor}
      className="overflow-clip"
      id={id}
      {...props}
    >
      {headingPrimaryText && (
        <div
          className={classNames({
            "w-full sm:w-7/12": variant === "double-entry",
          })}
        >
          <TableSectionTitles
            sectionId={sectionId}
            size={headingSize}
            headingSecondaryText={headingSecondaryText}
            headingSecondaryColor={
              overrideHeadingSecondaryColor || getAccentColor(backgroundColor)
            }
            headingPrimaryText={headingPrimaryText}
            headingPrimaryColor={
              overrideHeadingPrimaryColor || getAccentColor(backgroundColor)
            }
            showSliderHeading={
              columnCount > tableNumDesktopColumns && variant === "single-entry"
            }
            alignment={variant === "double-entry" ? "left" : "center"}
          />
        </div>
      )}

      {variant === "single-entry" ? (
        <SingleEntryComparisonTable
          backgroundColor={backgroundColor}
          numDesktopColumns={tableNumDesktopColumns}
          columnImages={tableColumnImages}
          rowHeadings={tableRowHeadings}
          data={tableData}
          sectionId={sectionId}
          outerContainer={outerContainer}
        />
      ) : (
        <div className="!mt-20">
          <DoubleEntryComparisonTable
            backgroundColor={backgroundColor}
            columnImages={tableColumnImages}
            rowHeadings={tableRowHeadings}
            data={tableData}
            outerContainer={outerContainer}
          />
        </div>
      )}
      <div className="pt-lg grid grid-cols-1 items-center gap-20 sm:grid-cols-2">
        {disclaimerText && (
          <Typography
            text={disclaimerText}
            font="grotesk"
            weight="medium"
            size="body-lg"
            color={getAccentColor(backgroundColor)}
            className="col-span-1 col-start-1 h-fit w-fit"
          />
        )}
        {buttonText && buttonLink && buttonBorderColor && (
          <PillButton
            style="bordered"
            size="large"
            color={buttonBorderColor}
            text={buttonText}
            overrideTextColor={getAccentColor(backgroundColor)}
            linkUrl={buttonLink}
            className="col-span-1 col-start-1 w-fit sm:col-start-2 sm:justify-self-end"
            trackingEvent={trackingEvent}
            trackingEventKey={trackingEventKey}
            trackingEventValue={trackingEventValue}
          />
        )}
      </div>
    </LayoutSection>
  )
}

export default ComparisonTableSection
